import React, { useEffect, useMemo, useState } from "react";
import { Input, Tag } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { distinctValues, fieldMetas, nameMap } from "./configFieldControl";
import { useWide } from "./split/SplitLayout";
import { ConfigPair } from "./split/ConfigPair";
import { BillingPair } from "./split/BillingPair";
import "./split/split.scss";
import "./split/config.scss";

const TAGGED = /\(cible actuelle\)/;

/** Column heads of the split layout (sticky on wide screens). */
function Heads({ wide, afterTitle }) {
  return (
    <div className={`sv-colheads${wide ? " sv-colheads--sticky" : ""}`}>
      <div className="sv-colhead sv-colhead--before">
        <HistoryOutlined className="sv-colhead-icon" />
        <div>
          <div className="sv-colhead-title">Avant — par établissement</div>
          <div className="sv-colhead-sub">
            ce que chaque établissement a aujourd'hui
          </div>
        </div>
      </div>
      <div className="sv-colhead sv-colhead--after">
        <CheckCircleOutlined className="sv-colhead-icon" />
        <div>
          <div className="sv-colhead-title">{afterTitle}</div>
          <div className="sv-colhead-sub">la valeur gardée — votre choix</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Step 2 — the resulting institution, field by field, as pairs of cards (same language as the
 * final check): AVANT = what every establishment has today, equal values grouped; APRÈS = the
 * choice (one radio per existing value, « Autre… ») and the resulting value. Fields that differ
 * come first; fields identical everywhere are folded under « Afficher les N champs identiques »
 * (they are decided: the shared value is kept). Then the billing rules, as a pair too.
 */
export function StepConfig({ preview, decisions, onChange, disabled }) {
  // the name is saved on blur, not per keystroke — one PUT per edit, never a race between letters
  const [name, setName] = useState(decisions.targetName || "");
  useEffect(() => setName(decisions.targetName || ""), [decisions.targetName]);
  const [showIdentical, setShowIdentical] = useState(false);
  const wide = useWide();

  const cfg = preview.config || { fields: {} };
  const fields = cfg.fields || {};
  const options = cfg.options || {};
  const names = useMemo(() => nameMap(cfg), [cfg]);
  const required = new Set(
    cfg.required || (preview.decisionsRequired || {}).config || [],
  );
  const chosen = decisions.config || {};
  const existing = preview.existingTarget;

  // the existing target takes part in the comparison — the server tags it, older previews do not
  const label = v =>
    existing &&
    String(v.sourceId) === String(existing._id) &&
    !TAGGED.test(v.sourceName || "")
      ? { ...v, sourceName: `${v.sourceName} (cible actuelle)` }
      : v;

  const allRows = useMemo(
    () =>
      fieldMetas(cfg).map(meta => {
        const f = fields[meta.field];
        const distinct = distinctValues((f.values || []).map(label));
        return { key: meta.field, meta, f, distinct };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cfg, fields, existing],
  );
  const conflicting = allRows.filter(r => r.f.conflict);
  const decidedIdentical = allRows.filter(
    r => !r.f.conflict && chosen[r.meta.field] !== undefined,
  );
  const identical = allRows.filter(
    r => !r.f.conflict && chosen[r.meta.field] === undefined,
  );
  const pendingCount = conflicting.filter(
    r => required.has(r.meta.field) && chosen[r.meta.field] === undefined,
  ).length;

  const currentNames = fields.name
    ? (fields.name.values || []).map(v => label(v).sourceName)
    : (preview.sources || []).map(s => s.name);
  if (existing && !currentNames.some(n => TAGGED.test(n)))
    currentNames.unshift(`${existing.name} (cible actuelle)`);

  const afterTitle = existing
    ? "Après — institution qui reçoit la fusion"
    : "Après — nouvelle institution";
  const donor =
    (preview.configDonor && preview.configDonor.name) ||
    "l'établissement de référence";

  const pair = r => (
    <ConfigPair
      key={r.key}
      meta={r.meta}
      f={r.f}
      distinct={r.distinct}
      chosen={chosen[r.meta.field]}
      required={required.has(r.meta.field)}
      names={names}
      options={options}
      disabled={disabled}
      stacked={!wide}
      decide={v => onChange({ config: { [r.meta.field]: v } })}
    />
  );

  // ONE keyed list: a field that moves between groups (identical → decided) keeps its state
  const list = [];
  if (conflicting.length)
    list.push(
      <div key="h-conflicts" className="cfg-group">
        <span className="cfg-group-title">
          Champs qui diffèrent entre les établissements
        </span>
        <span className="cfg-group-count">
          {pendingCount
            ? `${pendingCount} sur ${conflicting.length} encore à choisir`
            : `${conflicting.length} sur ${conflicting.length} choisis`}
        </span>
      </div>,
    );
  conflicting.forEach(r => list.push(pair(r)));
  if (decidedIdentical.length)
    list.push(
      <div key="h-decided" className="cfg-group">
        <span className="cfg-group-title">
          Champs identiques que vous avez changés
        </span>
        <span className="cfg-group-count">{decidedIdentical.length}</span>
      </div>,
    );
  decidedIdentical.forEach(r => list.push(pair(r)));
  if (identical.length)
    list.push(
      <div key="h-identical" className="cfg-folded">
        <Tag className="cfg-tag cfg-tag--same">identique partout</Tag>
        <span className="cfg-folded-text">
          {identical.length} champ(s) ont la même valeur sur tous les
          établissements : le résultat la reprend telle quelle.
          {!showIdentical && (
            <span className="cfg-folded-list">
              {" "}
              ({identical.map(r => r.meta.label).join(", ")})
            </span>
          )}
        </span>
        <button
          type="button"
          className="cfg-folded-btn"
          onClick={() => setShowIdentical(v => !v)}
        >
          {showIdentical
            ? `Masquer les ${identical.length} champs identiques`
            : `Afficher les ${identical.length} champs identiques`}
        </button>
      </div>,
    );
  if (showIdentical) identical.forEach(r => list.push(pair(r)));

  const nameStatus = decisions.targetName ? "typed" : "pending";

  return (
    <div className="sv-root cfg-root">
      <p className="sv-what cfg-what">
        <strong>Ce qui se passe ici : </strong>
        la configuration part de <strong>{donor}</strong>.{" "}
        {conflicting.length
          ? `${conflicting.length} champ(s) diffèrent entre les établissements`
          : "Aucun champ ne diffère entre les établissements"}
        {required.size ? ` et exigent un choix` : ""} ;{" "}
        {identical.length + decidedIdentical.length} champ(s) sont identiques
        partout. À gauche ce que chaque établissement a aujourd'hui, à droite ce
        que {existing ? "l'institution qui reçoit" : "la nouvelle institution"}{" "}
        aura : cochez une des valeurs existantes ou choisissez « Autre… » pour
        en saisir une nouvelle. Une valeur barrée à gauche est remplacée.
      </p>

      <Heads wide={wide} afterTitle={afterTitle} />

      <div className="cfg-pairs">
        {/* the name */}
        <div
          className={`cfg-duo cfg-duo--name${wide ? "" : " cfg-duo--stacked"}`}
          data-field="name"
        >
          <div className="cfg-card cfg-card--before">
            <div className="cfg-head cfg-head--before">
              {!wide && (
                <div className="sv-ribbon sv-ribbon--before">AVANT</div>
              )}
              <div className="cfg-head-title">Nom</div>
              <div className="cfg-head-sub">
                Avant — noms actuels des établissements :
              </div>
            </div>
            <div className="cfg-names">
              {currentNames.map((n, i) => (
                <span key={`${n}-${i}`} className="cfg-name-chip">
                  {n}
                </span>
              ))}
            </div>
          </div>
          <div className="cfg-card cfg-card--after">
            <div className="cfg-head cfg-head--after">
              {!wide && <div className="sv-ribbon sv-ribbon--after">APRÈS</div>}
              <div className="cfg-head-title">
                {existing
                  ? "Institution qui reçoit la fusion"
                  : "Nom de la nouvelle institution"}
              </div>
              <div className="cfg-head-sub">
                {existing
                  ? "une institution qui existe déjà"
                  : "le nom que tout le monde verra"}
              </div>
            </div>
            <div className="cfg-name-body">
              {existing ? (
                <div className="cfg-result cfg-result--same">
                  <div className="cfg-result-body">
                    <span className="cfg-result-value">
                      <span className="sv-val--new">{existing.name}</span>
                    </span>
                    <Tag className="cfg-tag cfg-tag--same">
                      existante — son nom ne change pas
                    </Tag>
                  </div>
                </div>
              ) : (
                <>
                  <Input
                    size="large"
                    disabled={disabled}
                    value={name}
                    status={decisions.targetName ? undefined : "error"}
                    onChange={e => setName(e.target.value)}
                    onBlur={() =>
                      name.trim() !== (decisions.targetName || "") &&
                      onChange({ targetName: name.trim() })
                    }
                    onPressEnter={e => e.target.blur()}
                    placeholder="ex. Malévoz"
                  />
                  <div className="cfg-name-state">
                    {nameStatus === "pending" ? (
                      <Tag
                        className="cfg-tag cfg-tag--pending"
                        icon={<ExclamationCircleOutlined />}
                      >
                        à choisir
                      </Tag>
                    ) : (
                      <Tag className="cfg-tag cfg-tag--typed">
                        valeur saisie
                      </Tag>
                    )}
                    <span className="cfg-holders">
                      enregistré quand vous quittez le champ
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {list}
      </div>

      <div className="cfg-section">
        <h3 className="sv-section-title">Règles de facturation</h3>
        <p className="sv-what">
          <strong>Ce qui se passe ici : </strong>
          {(preview.billings && (preview.billings.timeline || []).length) || 0
            ? `${
                preview.billings.timeline.length
              } règle(s) datée(s) réunies ; ${
                (preview.billings.conflicts || []).length
              } mois en conflit. `
            : "Aucune règle datée sur les établissements — le mode de facturation groupée choisi ci-dessus s'applique. "}
          Les règles sont réunies en une seule chronologie, pas choisies en bloc
          : reprendre la liste d'un seul établissement refacturerait
          l'historique des autres avec les mauvaises règles. Chaque mois peut
          ensuite être ajusté, ajouté ou retiré à droite.
        </p>
        <BillingPair
          bl={preview.billings || { timeline: [], conflicts: [] }}
          sources={preview.sources}
          existingTarget={existing}
          decided={decisions.billings}
          disabled={disabled}
          stacked={!wide}
          onSave={list => onChange({ billings: list })}
        />
      </div>
    </div>
  );
}
